import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import {
  Package, MapPin, Clock, Check, ArrowLeft, Phone,
  ChevronDown, ChevronUp, RotateCcw, Truck, AlertTriangle,
  FileText, Plus, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  envoye: { label: 'Nouveau', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  accepte: { label: 'Accepte', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  en_cours: { label: 'En cours', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  complete: { label: 'Termine', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  retourne: { label: 'Retourne', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const RETURN_REASONS = [
  'Bris mecanique',
  'Client absent',
  'Adresse introuvable',
  'Marchandise non prete',
  'Annule par le client',
  'Autre',
];

const MiniStopRow = ({ stop }) => {
  const isPickup = stop.stop_type === 'cueillette';
  return (
    <div className="bg-slate-800/60 rounded-lg p-2.5 space-y-1">
      <div className="flex items-center gap-2">
        <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded ${
          isPickup ? 'bg-emerald-500/20 text-emerald-400' : 'bg-purple-500/20 text-purple-400'
        }`}>
          {isPickup ? 'Cueillette' : 'Livraison'}
        </span>
        {stop.required_time && (
          <span className="text-xs font-mono text-slate-400">{stop.required_time}</span>
        )}
      </div>
      <p className="text-white text-sm font-semibold truncate">{stop.client_name}</p>
      <p className="text-slate-400 text-xs truncate">{stop.address}{stop.city ? `, ${stop.city}` : ''}</p>
    </div>
  );
};

// OrderBlockCard — shows both stops of a blocked order together.
// Buttons adapt to current status:
//   - status="envoye"  → "Accepter le bloc" / "Retourner"
//   - status="accepte" → "Ajouter au rapport" / "Nouveau rapport"
// After being added to a report, the 2 stops leave this list and appear
// individually inside the daily report (where the driver can reorder them).
const OrderBlockCard = ({ pickup, delivery, onAcceptBlock, onReturnBlock, onAddBlockToReport, onNewReportFromBlock }) => {
  const orderNum = pickup.order_number || delivery.order_number;
  const orderId = pickup.order_id;
  const requiredDate = pickup.required_date || delivery.required_date;
  const isPendingAccept = pickup.status === 'envoye';

  return (
    <div
      className="bg-slate-900 rounded-xl border-2 border-blue-500/40 overflow-hidden"
      data-testid={`driver-order-block-${orderId}`}
    >
      <div className="px-3 py-2 bg-blue-500/10 border-b border-blue-500/20 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Package size={16} className="text-blue-400 flex-shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wide text-blue-300">
            {isPendingAccept ? 'Nouvelle commande' : 'Commande acceptée'}
          </span>
          <span className="text-xs text-slate-400 truncate">{orderNum}</span>
        </div>
        {requiredDate && (
          <span className="px-2 py-0.5 text-[10px] rounded bg-slate-800 text-slate-300 font-mono flex-shrink-0">
            {requiredDate}
          </span>
        )}
      </div>
      <div className="p-3 space-y-2">
        <MiniStopRow stop={pickup} />
        <MiniStopRow stop={delivery} />
        {isPendingAccept ? (
          <div className="flex gap-2 pt-1">
            <Button
              onClick={() => onAcceptBlock(orderId)}
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold"
              data-testid={`block-accept-${orderId}`}
            >
              <Check size={16} className="mr-1" /> Accepter le bloc
            </Button>
            <Button
              variant="outline"
              onClick={() => onReturnBlock(pickup, delivery)}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid={`block-return-${orderId}`}
            >
              <RotateCcw size={16} className="mr-1" /> Retourner
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 pt-1">
            <Button
              onClick={() => onAddBlockToReport(pickup, delivery)}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
              data-testid={`block-add-${orderId}`}
            >
              <Plus size={16} className="mr-1" /> Ajouter à un rapport
            </Button>
            <Button
              variant="outline"
              onClick={() => onNewReportFromBlock(pickup, delivery)}
              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
              data-testid={`block-new-report-${orderId}`}
            >
              <Plus size={16} className="mr-1" /> Nouveau rapport
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const StopCard = ({ stop, onAccept, onReturn, onOpenReportDialog, onArchive }) => {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_CONFIG[stop.status] || STATUS_CONFIG.envoye;
  const isPickup = stop.stop_type === 'cueillette';
  const isNew = stop.status === 'envoye';
  const isAccepted = stop.status === 'accepte';
  const isLinked = !!stop.linked_report_id;
  const isDone = stop.status === 'complete' || stop.status === 'retourne';

  return (
    <div
      className={`bg-slate-900 border rounded-xl overflow-hidden ${
        isNew ? 'border-blue-500/40 ring-1 ring-blue-500/20' :
        stop.status === 'en_cours' ? 'border-green-500/40' : 'border-slate-800'
      }`}
      data-testid={`stop-card-${stop.id}`}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center gap-3 text-left"
        data-testid={`stop-toggle-${stop.id}`}
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          isPickup ? 'bg-blue-500/20' : 'bg-amber-500/20'
        }`}>
          {isPickup ? <Package size={20} className="text-blue-400" /> : <Truck size={20} className="text-amber-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${status.color}`}>
              {status.label}
            </span>
            <span className="text-xs text-slate-500 uppercase">{isPickup ? 'Cueillette' : 'Livraison'}</span>
            {stop.priority === 'urgent' && (
              <AlertTriangle size={14} className="text-red-400" />
            )}
          </div>
          <p className="text-white font-semibold truncate">{stop.client_name}</p>
          <p className="text-slate-400 text-sm truncate">{stop.address}, {stop.city}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {stop.required_time && (
            <span className="text-slate-400 text-sm font-mono">{stop.required_time}</span>
          )}
          {expanded ? <ChevronUp size={18} className="text-slate-500" /> : <ChevronDown size={18} className="text-slate-500" />}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-800 pt-3">
          {/* Details */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 text-slate-400">
              <Clock size={14} />
              <span>Date: {stop.required_date}</span>
            </div>
            {stop.contact_name && (
              <div className="flex items-center gap-2 text-slate-400">
                <Phone size={14} />
                <span>{stop.contact_name}</span>
              </div>
            )}
          </div>

          {stop.contact_phone && (
            <a
              href={`tel:${stop.contact_phone}`}
              className="flex items-center gap-2 text-blue-400 text-sm hover:underline"
              data-testid={`stop-call-${stop.id}`}
            >
              <Phone size={14} />
              {stop.contact_phone}
            </a>
          )}

          {/* Items */}
          {stop.items && stop.items.length > 0 && (
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase font-bold mb-1">Articles</p>
              {stop.items.map((item, i) => (
                <div key={i} className="flex justify-between text-sm py-0.5">
                  <span className="text-white font-mono">{item.bon_number}</span>
                  <span className="text-slate-400">{item.quantity || item.description || ''}</span>
                </div>
              ))}
            </div>
          )}

          {stop.notes && (
            <p className="text-sm text-slate-400 italic bg-slate-800/30 p-2 rounded">{stop.notes}</p>
          )}

          {/* Times */}
          {(stop.arrival_time || stop.departure_time) && (
            <div className="flex gap-4 text-xs text-slate-400">
              {stop.arrival_time && <span>Arrivee: {new Date(stop.arrival_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</span>}
              {stop.departure_time && <span>Depart: {new Date(stop.departure_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</span>}
            </div>
          )}

          {/* Linked report info */}
          {isLinked && !isDone && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-2 text-xs text-green-400 flex items-center gap-2">
              <FileText size={14} />
              Ajoute au rapport — gerez l'arrivee et le depart depuis votre rapport
            </div>
          )}

          {/* Action Buttons — simplified flow */}
          <div className="flex flex-wrap gap-2 pt-1">
            {/* Step 1: Accept */}
            {isNew && (
              <Button
                onClick={() => onAccept(stop.id)}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold"
                data-testid={`stop-accept-${stop.id}`}
              >
                <Check size={16} className="mr-1" /> Accepter
              </Button>
            )}

            {/* Step 2: After accept, show add-to-report buttons */}
            {isAccepted && !isLinked && (
              <>
                <Button
                  onClick={() => onOpenReportDialog(stop, 'existing')}
                  className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold"
                  data-testid={`stop-add-report-${stop.id}`}
                >
                  <FileText size={16} className="mr-1" /> Ajouter au rapport
                </Button>
                <Button
                  onClick={() => onOpenReportDialog(stop, 'new')}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold"
                  data-testid={`stop-new-report-${stop.id}`}
                >
                  <Plus size={16} className="mr-1" /> Nouveau rapport
                </Button>
              </>
            )}

            {/* Return button (always available if not done) */}
            {!isDone && (
              <Button
                variant="outline"
                onClick={() => onReturn(stop)}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid={`stop-return-${stop.id}`}
              >
                <RotateCcw size={16} className="mr-1" /> Retourner
              </Button>
            )}

            {/* Archive button for completed/returned stops */}
            {isDone && onArchive && (
              <Button
                variant="outline"
                onClick={() => onArchive(stop)}
                className="border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-amber-400"
                data-testid={`stop-archive-${stop.id}`}
              >
                <CheckCircle2 size={16} className="mr-1" /> Archiver
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const DriverOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, patch, post } = useApi();
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [returnDialog, setReturnDialog] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [reportDialog, setReportDialog] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState('');

  const fetchStops = useCallback(async () => {
    try {
      // Quand le filtre "Terminés" est actif, on demande aussi les commandes
      // que le driver a archivées automatiquement en complétant un rapport.
      // Sinon le driver voit "Aucune commande" alors qu'il a un historique.
      const params = filter === 'complete' ? { include_archived: true } : {};
      const result = await get('/dispatch/driver/stops', params);
      if (result.success && result.data) {
        setStops(result.data.stops || []);
      }
    } catch (err) {
      toast.error('Erreur chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [get, filter]);

  const fetchReports = useCallback(async () => {
    try {
      const result = await get('/daily-reports?status=draft');
      if (result.success && result.data) {
        setReports(Array.isArray(result.data) ? result.data : result.data.reports || []);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  }, [get]);

  useEffect(() => {
    fetchStops();
    fetchReports();
    // Auto-refresh every 30 seconds to pick up newly assigned commands
    // from the dispatcher without requiring a manual page reload.
    const interval = setInterval(() => {
      fetchStops();
    }, 30000);
    // Refresh immédiat quand l'app revient au premier plan / la page redevient visible.
    // Résout le bug "orders stuck en_cours" quand un driver termine un rapport et
    // revient sur cette page avant le prochain tick 30s : le status côté dispatch
    // vient d'être synchronisé côté backend et doit être re-fetché immédiatement.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchStops();
        fetchReports();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchStops, fetchReports]);

  const handleAccept = async (stopId) => {
    try {
      const result = await patch(`/dispatch/driver/stops/${stopId}/accept`);
      if (result.success) {
        toast.success('Commande acceptee — ajoutez-la a un rapport');
        fetchStops();
      }
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  const handleAcceptBlock = async (orderId) => {
    try {
      const result = await patch(`/dispatch/driver/orders/${orderId}/accept`);
      if (result.success) {
        toast.success('Commande acceptée — ajoutez-la à un rapport');
        fetchStops();
      }
    } catch (err) {
      toast.error(err.message || 'Erreur acceptation du bloc');
    }
  };

  const handleReturnBlock = (pickup, delivery) => {
    // For Étape 4, returning a block opens the same return dialog on the pickup;
    // upon submit, we'll return both stops with the same reason (handled in Étape 5).
    // For now we just open the dialog on the pickup as a placeholder so the driver
    // can return at least the pickup. Full block-return UX will land in Étape 5.
    setReturnDialog({ ...pickup, _isBlock: true, _siblingId: delivery.id });
    setReturnReason('');
    setCustomReason('');
  };

  // Add an accepted BLOCK (both stops) to an existing report.
  // The dialog re-uses the existing "add to report" UI but with both stop IDs.
  const handleAddBlockToReport = (pickup, delivery) => {
    fetchReports();
    setReportDialog({
      ...pickup,
      client_name: `${pickup.client_name} + ${delivery.client_name}`,
      _isBlock: true,
      _siblingId: delivery.id,
    });
    setSelectedReportId('');
  };

  // Create a NEW daily report and immediately add both stops of the block to it.
  const handleNewReportFromBlock = async (pickup, delivery) => {
    try {
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const result = await post('/daily-reports', { date: dateStr });
      if (result.success && result.data) {
        const reportId = result.data.id;
        await post(`/dispatch/driver/stops/${pickup.id}/add-to-report`, { report_id: reportId });
        await post(`/dispatch/driver/stops/${delivery.id}/add-to-report`, { report_id: reportId });
        toast.success('Rapport créé et commande ajoutée');
        navigate(`/daily-report?date=${dateStr}`);
      }
    } catch (err) {
      toast.error(err.message || 'Erreur création du rapport');
    }
  };

  const handleReturnSubmit = async () => {
    if (!returnDialog) return;
    const reason = returnReason === 'Autre' ? customReason : returnReason;
    if (!reason) {
      toast.error('Veuillez selectionner une raison');
      return;
    }
    try {
      const result = await post(`/dispatch/driver/stops/${returnDialog.id}/return`, { reason });
      if (result.success) {
        // If this was a block return, also return the sibling stop with the same reason
        if (returnDialog._isBlock && returnDialog._siblingId) {
          try {
            await post(`/dispatch/driver/stops/${returnDialog._siblingId}/return`, { reason });
          } catch (e) {
            console.error('Sibling return failed:', e);
          }
        }
        toast.success(returnDialog._isBlock ? 'Commande retournée au répartiteur' : 'Arret retourne au repartiteur');
        setReturnDialog(null);
        setReturnReason('');
        setCustomReason('');
        fetchStops();
      }
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  const handleOpenReportDialog = (stop, mode) => {
    if (mode === 'new') {
      handleCreateNewReport(stop);
    } else {
      fetchReports();
      setReportDialog(stop);
      setSelectedReportId('');
    }
  };

  const handleCreateNewReport = async (stop) => {
    try {
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const result = await post('/daily-reports', { date: dateStr });
      if (result.success && result.data) {
        const reportId = result.data.id;
        // Add stop to the new report
        await post(`/dispatch/driver/stops/${stop.id}/add-to-report`, { report_id: reportId });
        toast.success('Rapport cree et arret ajoute');
        navigate(`/daily-report?date=${dateStr}`);
      }
    } catch (err) {
      toast.error(err.message || 'Erreur creation du rapport');
    }
  };

  const handleAddToReport = async () => {
    if (!reportDialog || !selectedReportId) return;
    try {
      const result = await post(`/dispatch/driver/stops/${reportDialog.id}/add-to-report`, {
        report_id: selectedReportId,
      });
      if (result.success) {
        // If this was a block, also add the sibling stop to the same report
        if (reportDialog._isBlock && reportDialog._siblingId) {
          try {
            await post(`/dispatch/driver/stops/${reportDialog._siblingId}/add-to-report`, {
              report_id: selectedReportId,
            });
          } catch (e) {
            console.error('Sibling add-to-report failed:', e);
          }
        }
        toast.success(reportDialog._isBlock ? 'Commande ajoutée au rapport' : 'Arret ajoute au rapport');
        setReportDialog(null);
        setSelectedReportId('');
        fetchStops();
      }
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  const handleArchiveStop = async (stop) => {
    try {
      const result = await patch(`/dispatch/driver/stops/${stop.id}/archive`);
      if (result.success) {
        setStops(prev => prev.filter(s => s.id !== stop.id));
        toast.success('Commande archivée');
      }
    } catch (err) {
      toast.error(err.message || 'Erreur archivage');
    }
  };

  // Group "envoye|accepte + blocked" stops by order_id for block-card display.
  // The block stays grouped UNTIL the driver adds it to a report (or returns it).
  // After being added to a report, the 2 stops disappear from this list and
  // appear inside the daily report where they can be reordered.
  const groupStopsForDisplay = (list) => {
    const byOrder = new Map();
    const solos = [];
    for (const s of list) {
      const isGroupable = (
        (s.status === 'envoye' || s.status === 'accepte') &&
        s.block_status === 'blocked' &&
        s.order_id
      );
      if (!isGroupable) {
        solos.push(s);
        continue;
      }
      if (!byOrder.has(s.order_id)) byOrder.set(s.order_id, []);
      byOrder.get(s.order_id).push(s);
    }
    const result = [];
    for (const [orderId, items] of byOrder.entries()) {
      const pickup = items.find(x => x.stop_type === 'cueillette');
      const delivery = items.find(x => x.stop_type === 'livraison');
      // Block requires BOTH stops AND same status (don't show a mixed-status block)
      if (pickup && delivery && pickup.status === delivery.status) {
        result.push({ type: 'block', orderId, pickup, delivery });
      } else {
        items.forEach(x => result.push({ type: 'stop', stop: x }));
      }
    }
    solos.forEach(s => result.push({ type: 'stop', stop: s }));
    return result;
  };

  const filteredStops = stops.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['envoye', 'accepte', 'en_cours'].includes(s.status);
    if (filter === 'complete') return s.status === 'complete';
    return true;
  });

  const activeCount = stops.filter(s => ['envoye', 'accepte', 'en_cours'].includes(s.status)).length;
  const newCount = stops.filter(s => s.status === 'envoye').length;

  if (loading) {
    return (
      <div className="page-wrapper p-4" data-testid="orders-loading">
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-900 rounded-xl p-4 animate-pulse">
              <div className="h-5 bg-slate-800 rounded w-2/3 mb-2" />
              <div className="h-4 bg-slate-800 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper" data-testid="driver-orders-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Mes commandes</h1>
            <p className="text-slate-400 text-sm">
              {newCount > 0 && <span className="text-blue-400 font-semibold">{newCount} nouvelle{newCount > 1 ? 's' : ''}</span>}
              {newCount > 0 && activeCount > newCount && ' · '}
              {activeCount > 0 && `${activeCount} actif${activeCount > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 pt-2 pb-3 flex gap-2">
        {[
          { key: 'all', label: filter === 'all' ? `Tous (${stops.length})` : 'Tous' },
          { key: 'active', label: filter === 'active' ? `Actifs (${activeCount})` : 'Actifs' },
          { key: 'complete', label: filter === 'complete' ? `Termines (${stops.length})` : 'Termines' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-amber-500 text-black'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Stops */}
      <div className="px-4 space-y-3 pb-24">
        {filteredStops.length === 0 && (
          <div className="text-center py-12">
            <Package size={48} className="text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400">Aucune commande</p>
          </div>
        )}
        {groupStopsForDisplay(filteredStops).map(item => (
          item.type === 'block' ? (
            <OrderBlockCard
              key={`block-${item.orderId}`}
              pickup={item.pickup}
              delivery={item.delivery}
              onAcceptBlock={handleAcceptBlock}
              onReturnBlock={handleReturnBlock}
              onAddBlockToReport={handleAddBlockToReport}
              onNewReportFromBlock={handleNewReportFromBlock}
            />
          ) : (
            <StopCard
              key={item.stop.id}
              stop={item.stop}
              onAccept={handleAccept}
              onReturn={(s) => { setReturnDialog(s); setReturnReason(''); setCustomReason(''); }}
              onOpenReportDialog={handleOpenReportDialog}
              onArchive={handleArchiveStop}
            />
          )
        ))}
      </div>

      {/* Return Dialog */}
      <Dialog open={!!returnDialog} onOpenChange={(open) => !open && setReturnDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Retourner l'arret</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-400">
              Retourner <span className="text-white font-semibold">{returnDialog?.client_name}</span> au repartiteur
            </p>
            <div className="space-y-2">
              {RETURN_REASONS.map(r => (
                <button
                  key={r}
                  onClick={() => setReturnReason(r)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    returnReason === r
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                  data-testid={`return-reason-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
            {returnReason === 'Autre' && (
              <Textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Decrivez la raison..."
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="return-custom-reason"
              />
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-slate-700 text-slate-300">Annuler</Button>
            </DialogClose>
            <Button
              onClick={handleReturnSubmit}
              className="bg-red-600 hover:bg-red-500"
              data-testid="return-submit"
            >
              Retourner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Report Dialog */}
      <Dialog open={!!reportDialog} onOpenChange={(open) => !open && setReportDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter au rapport</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-400">
              Ajouter <span className="text-white font-semibold">{reportDialog?.client_name}</span> a un rapport existant
            </p>
            {reports.length === 0 ? (
              <p className="text-sm text-amber-400">Aucun rapport en cours. Utilisez "Nouveau rapport" a la place.</p>
            ) : (
              <div className="space-y-2">
                {reports.map(r => (
                  <button
                    key={r.id}
                    onClick={() => setSelectedReportId(r.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedReportId === r.id
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                    data-testid={`report-option-${r.id}`}
                  >
                    <span className="font-semibold">Rapport du {r.date}</span>
                    <span className="text-slate-500 ml-2">({(r.stops || []).length} arrets)</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-slate-700 text-slate-300">Annuler</Button>
            </DialogClose>
            <Button
              onClick={handleAddToReport}
              disabled={!selectedReportId}
              className="bg-green-600 hover:bg-green-500"
              data-testid="add-to-report-confirm"
            >
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverOrdersPage;
